var React = require('react');
var { createElement: h, useEffect, useLayoutEffect, useRef, useState } = React;
var { CodingLines } = require('./CodingLines');
var NodeIcon = require('./NodeIcon');
var AiFlowChatPanel = require('./AiFlowChatPanel');
var { getInsertNodeKindOptions, isSystemObjectKind, isToolNodeKind } = require('../lib/insertNodeKinds');

function AiFlowPromptBalloon(props) {
  var sourceTitle = props.sourceTitle || 'Node';
  var targetTitle = props.targetTitle || '';
  var prompt = props.prompt || '';
  var loading = !!props.loading;
  var error = props.error || '';
  var position = props.position || { left: 0, top: 0, side: 'left' };
  var onPromptChange = props.onPromptChange || function() {};
  var onGenerate = props.onGenerate || function() {};
  var onClose = props.onClose || function() {};
  var agentMode = !!props.agentMode;
  var onAgentModeChange = props.onAgentModeChange || function() {};
  var modelTier = props.modelTier || 'balanced';
  var onModelTierChange = props.onModelTierChange || function() {};
  var responseFields = Array.isArray(props.responseFields) ? props.responseFields : [''];
  var onResponseFieldsChange = props.onResponseFieldsChange || function() {};
  var nodeKind = props.nodeKind || 'custom';
  var onNodeKindChange = props.onNodeKindChange || function() {};
  var isInsertBetween = !!props.isInsertBetween;
  // Opened from a Tool Agent's "+ add tool" connector. This is what makes the
  // "tool node" kind available (a tool node only wires correctly from here). The
  // node is generated in tool mode when the selected kind is 'tool'.
  var fromToolAnchor = !!props.toolMode;
  var toolMode = isToolNodeKind(nodeKind);
  // Single-node chat plumbing (custom/agent kinds reuse the planner stream).
  var singleNodeContext = props.singleNodeContext || null;
  var onNodeReady = props.onNodeReady || function() {};
  // Lets the balloon follow the canvas while it pans (pan lives in a ref, so no
  // React render happens). Absent outside the canvas → the balloon stays put.
  var subscribeToPan = props.subscribeToPan || null;
  var popoverRef = useRef(null);
  var modalRef = useRef(null);

  // Chat mode: custom/agent kinds open a probing + Q&A conversation in-place
  // (config captured at submit) instead of one-shot generating. System kinds
  // (variable, condition, …) keep creating instantly via onGenerate.
  var [chatMode, setChatMode] = useState(false);
  var [chatConfig, setChatConfig] = useState(null);

  // `anchor` is the wire's start — the source node's edge, in canvas coords.
  // `offset` is the screen gap from it to the balloon's top-left, and `dragRef`
  // whatever the user has since dragged the card by. Screen position = anchor
  // (through the live pan/zoom) + offset + drag.
  var anchor = position.anchor || null;
  var offset = position.offset || { left: 0, top: 0 };
  var panRef = useRef({ x: 0, y: 0, z: 1 });
  var dragRef = useRef({ x: 0, y: 0 });
  var wirePathRef = useRef(null);
  var wireDotRef = useRef(null);

  // Positions the card and redraws its wire. Done imperatively so a pan or a
  // drag never has to re-render the (potentially chat-heavy) card body.
  function layout() {
    var el = popoverRef.current;
    if (!el) return;
    var pan = panRef.current;
    var drag = dragRef.current;
    // Without a pan feed we can't re-derive the anchor's screen spot, so fall
    // back to the position captured when the balloon opened.
    var tracking = !!(anchor && subscribeToPan);
    var x = (tracking ? (anchor.x * pan.z) + pan.x + offset.left : position.left) + drag.x;
    var y = (tracking ? (anchor.y * pan.z) + pan.y + offset.top : position.top) + drag.y;
    el.style.transform = 'translate(' + Math.round(x) + 'px, ' + Math.round(y) + 'px)';

    var modal = modalRef.current;
    if (!anchor || !modal || !wirePathRef.current) return;
    // Wire endpoints in card-local coords: from the source node's edge to a
    // fixed spot high on whichever card edge faces it (the header corner), so
    // the wire visibly swings up to the card instead of degenerating into a
    // straight line that hides under the node's existing edge.
    var startX = -(offset.left + drag.x);
    var startY = -(offset.top + drag.y);
    var endX = startX <= modal.offsetWidth / 2 ? 0 : modal.offsetWidth;
    var endY = 36;
    // Same curve as the canvas's smoothPath: each end exits along its anchor
    // direction — the node side the wire leaves from, and the card edge normal.
    var dx = endX - startX;
    var dy = endY - startY;
    var stem = Math.max(60, Math.min(150, Math.sqrt(dx * dx + dy * dy) * 0.45));
    var c1x = startX + (anchor.dirX || 0) * stem;
    var c1y = startY + (anchor.dirY || 0) * stem;
    var c2x = endX + (endX === 0 ? -stem : stem);
    var c2y = endY;
    wirePathRef.current.setAttribute('d',
      'M' + startX + ',' + startY +
      ' C' + c1x + ',' + c1y +
      ' ' + c2x + ',' + c2y +
      ' ' + endX + ',' + endY);
    wireDotRef.current.setAttribute('cx', startX);
    wireDotRef.current.setAttribute('cy', startY);
  }

  // Always call the newest closure — the pan subscriber and the ResizeObserver
  // outlive the render that created them.
  var layoutRef = useRef(layout);
  layoutRef.current = layout;

  // Re-place after every render (the card grows when Agent node is toggled on).
  useLayoutEffect(function() { layoutRef.current(); });

  useLayoutEffect(function() {
    if (!subscribeToPan) return;
    // Fires immediately with the current pan/zoom, then on every pan frame.
    return subscribeToPan(function(panX, panY, zoom) {
      panRef.current = { x: panX, y: panY, z: zoom };
      layoutRef.current();
    });
  }, [subscribeToPan]);

  // The textarea is user-resizable, which changes the wire's attach point
  // without ever re-rendering.
  useLayoutEffect(function() {
    var el = modalRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    var observer = new ResizeObserver(function() { layoutRef.current(); });
    observer.observe(el);
    return function() { observer.disconnect(); };
  }, []);

  // Drag the card by its header. The wire stretches to follow it.
  function onHeaderMouseDown(event) {
    if (event.button !== 0) return;
    if (event.target.closest('button')) return; // let × through
    event.preventDefault();
    var originX = event.clientX;
    var originY = event.clientY;
    var start = { x: dragRef.current.x, y: dragRef.current.y };

    function onMove(moveEvent) {
      dragRef.current = {
        x: start.x + (moveEvent.clientX - originX),
        y: start.y + (moveEvent.clientY - originY)
      };
      layoutRef.current();
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  var kindOptions = getInsertNodeKindOptions({ allowToolNode: fromToolAnchor });
  var isSystemKind = isSystemObjectKind(nodeKind);
  var canSubmit = isSystemKind ? true : !!String(prompt || '').trim();

  function submit() {
    if (isSystemKind) { onGenerate(); return; }
    var text = String(prompt || '').trim();
    if (!text) return;
    // Tool nodes SKIP the single-node planner discovery (probe + Q&A chat):
    // the typed description goes straight to tool-mode codegen. Tool nodes are
    // always agent nodes with the fixed "output" response field, and the tool
    // contract (command channel, generalized input) is enforced by codegen —
    // there is nothing for the planner to decide.
    if (toolMode) {
      onNodeReady({ prompt: text, agentMode: true, responseFields: ['output'] });
      return;
    }
    setChatConfig({
      prompt: text,
      agentMode: agentMode,
      modelTier: modelTier,
      responseFields: responseFields
    });
    setChatMode(true);
  }

  // Only the × button and Escape close the balloon. An outside click never
  // dismisses it — that would silently discard a typed prompt (or an in-flight
  // chat) the moment the user clicked the canvas.
  useEffect(function() {
    function onDocumentKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onDocumentKeyDown);
    return function() {
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [onClose]);

  function onKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }

  // The heading states what this balloon will create, with the node it attaches
  // to underlined. Every mode gets the same shape, so there is no subtitle.
  function renderTitle() {
    function node(title, key) {
      return h('span', { key: key, className: 'ai-flow-popover-title-node' }, title);
    }
    // Nesting a Tool Calling node as a tool of another Tool Calling node.
    if (isSystemKind && fromToolAnchor) {
      return ['Nest a tool inside ', node(sourceTitle, 'src')];
    }
    if (toolMode) {
      return ['New tool for ', node(sourceTitle, 'src')];
    }
    if (isInsertBetween && targetTitle) {
      return [
        'Insert node between ',
        node(sourceTitle, 'src'),
        ' and ',
        node(targetTitle, 'tgt')
      ];
    }
    if (isSystemKind) {
      return ['Add system node after ', node(sourceTitle, 'src')];
    }
    return ['Next node after ', node(sourceTitle, 'src')];
  }

  // Two side-by-side fields, each a label above its control: the "Node type"
  // dropdown and, for non-system kinds, the "Agent node" toggle. System kinds
  // leave the second column empty.
  function renderSettings() {
    var columns = [
      h('div', { key: 'type', className: 'ai-flow-popover-setting-col' },
        h('label', { className: 'ai-flow-popover-setting-label', htmlFor: 'ai-flow-kind' }, 'Node type'),
        h('select', {
          id: 'ai-flow-kind',
          className: 'ai-flow-popover-kind-select',
          value: nodeKind,
          onMouseDown: function(e) { e.stopPropagation(); },
          onChange: function(e) { onNodeKindChange(e.target.value); }
        },
          kindOptions.map(function(opt) {
            return h('option', { key: opt.value, value: opt.value }, opt.label);
          })
        )
      )
    ];

    if (!isSystemKind) {
      // Tool nodes are ALWAYS agent nodes (chat() interprets the calling
      // agent's command + input generally) — the toggle locks to On.
      var toggleLocked = toolMode;
      columns.push(
        h('div', { key: 'agent', className: 'ai-flow-popover-setting-col' },
          h('span', { className: 'ai-flow-popover-setting-label' }, 'Agent node'),
          h('button', {
            type: 'button',
            className: 'ai-flow-popover-toggle-box',
            onMouseDown: function(e) {
              e.preventDefault(); e.stopPropagation();
              if (toggleLocked) return;
              onAgentModeChange(!agentMode);
            },
            title: toggleLocked
              ? 'Tool nodes are always agent nodes'
              : (agentMode ? 'Switch to custom node mode' : 'Switch to agent node mode')
          },
            h('span', {
              className: 'ai-flow-popover-mode-toggle' + ((agentMode || toggleLocked) ? ' ai-flow-popover-mode-toggle-on' : '')
            },
              h('span', { className: 'ai-flow-popover-mode-toggle-knob' })
            ),
            h('span', { className: 'ai-flow-popover-toggle-text' }, (agentMode || toggleLocked) ? 'On' : 'Off')
          )
        )
      );
    }

    return h('div', { className: 'ai-flow-popover-settings' }, columns);
  }

  function renderSystemPreview() {
    var match = kindOptions.find(function(o) { return o.value === nodeKind; });
    if (!match || !match.templateIcon) return null;
    return h('div', { className: 'ai-flow-popover-system-preview' },
      h('span', { className: 'ai-flow-popover-system-preview-icon' },
        h(NodeIcon, { type: match.templateIcon, size: 18, strokeWidth: 1.8 })
      ),
      h('span', { className: 'ai-flow-popover-system-preview-label' }, match.label)
    );
  }

  // Shell only — layout() draws the actual path, so panning and dragging move
  // the wire without a React render. Never give this a width/height of 0: SVG
  // treats a zero-sized outermost element as "do not render", overflow and all.
  function renderWire() {
    if (!anchor) return null;
    return h('svg', { className: 'ai-flow-popover-wire' },
      h('path', { ref: wirePathRef, className: 'ai-flow-popover-wire-path' }),
      h('circle', { ref: wireDotRef, className: 'ai-flow-popover-wire-dot', r: 3.5 })
    );
  }

  return h('div', {
    ref: popoverRef,
    className: 'ai-flow-popover ai-flow-popover-' + (position.side || 'left'),
    onMouseDown: function(e) { e.stopPropagation(); },
    onTouchStart: function(e) { e.stopPropagation(); }
  },
    renderWire(),
    h('div', { ref: modalRef, className: 'ai-flow-popover-modal' },
      h('div', { className: 'ai-flow-popover-header', onMouseDown: onHeaderMouseDown },
        h('div', { className: 'ai-flow-popover-title-text' },
          h('div', { className: 'ai-flow-popover-eyebrow' }, 'AI Flow Builder'),
          h('div', { className: 'ai-flow-popover-title' }, renderTitle())
        ),
        !loading && h('button', { className: 'ai-flow-popover-close', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onClose(); }, title: 'Close' }, '×')
      ),
      chatMode
        ? h(AiFlowChatPanel, {
            initialPrompt: chatConfig ? chatConfig.prompt : prompt,
            agentMode: chatConfig ? chatConfig.agentMode : agentMode,
            modelTier: chatConfig ? chatConfig.modelTier : modelTier,
            responseFields: chatConfig ? chatConfig.responseFields : responseFields,
            singleNodeContext: singleNodeContext,
            // The user's explicit Agent-node toggle is AUTHORITATIVE when ON:
            // the planner only receives it as a prompt hint and its <node
            // agent="false"> decision used to silently win, producing a
            // non-agent node despite the toggle. (Toggle OFF still lets the
            // planner upgrade to agent when the task genuinely needs chat().)
            // Tool nodes additionally pin the response field to "output".
            onNodeReady: function(spec) {
              var s = spec || {};
              var userWantsAgent = chatConfig ? !!chatConfig.agentMode : agentMode;
              if (userWantsAgent && !s.agentMode) {
                s = Object.assign({}, s, { agentMode: true });
              }
              if (toolMode) {
                // Tool nodes are ALWAYS agent nodes with the fixed "output" field.
                s = Object.assign({}, s, { agentMode: true, responseFields: ['output'] });
              }
              onNodeReady(s);
            },
            onClose: onClose
          })
        : loading
        ? h('div', { className: 'ai-flow-popover-coding' },
            h('div', { className: 'ai-flow-popover-coding-status' },
              h('span', { className: 'ai-flow-popover-coding-spinner' }),
              h('span', { className: 'ai-flow-popover-coding-label' }, 'Coding')
            ),
            h(CodingLines, null)
          )
        : [
            h('div', { key: 'body', className: 'ai-flow-popover-body' },
              renderSettings(),
              !isSystemKind && h('div', { className: 'ai-flow-popover-section-label' },
                toolMode ? 'Tool description' : 'Description'
              ),
              isSystemKind ? renderSystemPreview() : h('textarea', {
                className: 'ai-flow-popover-input',
                rows: 3,
                placeholder: 'e.g. classify sentiment and return label + confidence',
                value: prompt,
                onChange: function(e) { onPromptChange(e.target.value); },
                onKeyDown: onKeyDown,
                autoFocus: true,
                spellCheck: false
              }),
              !isSystemKind ? h('div', { className: 'ai-flow-popover-hint' },
                h('span', { className: 'ai-flow-popover-hint-icon' }, '↳'),
                h('span', null, 'Be specific — the AI uses this to wire inputs and outputs.')
              ) : null,
              !isSystemKind && agentMode ? h('div', { className: 'ai-flow-popover-fields-section' },
                h('div', { className: 'ai-flow-popover-fields-header' },
                  h('span', { className: 'ai-flow-popover-field-label' }, 'Response Fields'),
                  h('button', {
                    className: 'ai-flow-popover-fields-add',
                    onMouseDown: function(e) {
                      e.preventDefault(); e.stopPropagation();
                      onResponseFieldsChange(responseFields.concat(['']));
                    },
                    title: 'Add field'
                  }, '+ Add')
                ),
                responseFields.map(function(fieldVal, idx) {
                  return h('div', { key: idx, className: 'ai-flow-popover-field-row' },
                    h('input', {
                      className: 'ai-flow-popover-field-input',
                      type: 'text',
                      placeholder: 'field_name',
                      value: fieldVal,
                      onChange: function(e) {
                        var next = responseFields.slice();
                        next[idx] = e.target.value;
                        onResponseFieldsChange(next);
                      },
                      spellCheck: false
                    }),
                    responseFields.length > 1 ? h('button', {
                      className: 'ai-flow-popover-field-remove',
                      onMouseDown: function(e) {
                        e.preventDefault(); e.stopPropagation();
                        var next = responseFields.filter(function(_, i) { return i !== idx; });
                        onResponseFieldsChange(next);
                      },
                      title: 'Remove field'
                    }, '×') : null
                  );
                })
              ) : null,
              error ? h('div', { className: 'ai-flow-popover-error' }, error) : null
            ),
            h('div', { key: 'actions', className: 'ai-flow-popover-actions' },
              h('button', {
                className: 'ai-flow-popover-btn ai-flow-popover-btn-secondary',
                onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onClose(); }
              }, 'Cancel'),
              h('button', {
                className: 'ai-flow-popover-btn ai-flow-popover-btn-primary',
                onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); submit(); },
                disabled: !canSubmit
              },
                isSystemKind
                  ? (isInsertBetween ? 'Insert' : 'Add node')
                  : 'Create node'
              )
            )
          ]
    ),
  );
}

module.exports = AiFlowPromptBalloon;
